import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * GLOBAL AJAX CART HANDLER
 * Handles ALL add-to-cart actions for forms marked with data-ajax-cart
 */
document.addEventListener('submit', async (e) => {
  const form = e.target;

  // Only intercept AJAX-enabled forms
  if (!form.matches('form[data-ajax-cart]')) return;

  e.preventDefault();

  const formData = new FormData(form);

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (res.ok) {
      // Notify drawer + global listeners
      document.dispatchEvent(
        new CustomEvent(CartAddEvent.eventName)
      );
    }
  } catch (err) {
    console.error('Add to cart failed', err);
  }
});

/**
 * CART DRAWER COMPONENT
 * Handles its own auto-refresh when CartAddEvent fires
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#initEmptyCartNav();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = async () => {
    await this.#refreshCartHtml();

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Fetches updated cart HTML via Shopify Section Rendering API
   */
  async #refreshCartHtml() {
    const itemsComponent = this.querySelector('cart-items-component');
    const sectionId = itemsComponent?.dataset.sectionId;

    if (!sectionId) return;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString(), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const sectionWrapper =
        doc.getElementById(`shopify-section-${sectionId}`) || doc;

      const newItemsComponent = sectionWrapper.querySelector(
        `cart-items-component[data-section-id="${sectionId}"]`
      );

      if (!newItemsComponent) return;

      itemsComponent.replaceWith(newItemsComponent);

      // Re-bind navigation for EMPTY CART UI
      this.#initEmptyCartNav();
    } catch (e) {
      console.error('Cart drawer refresh failed', e);
    }
  }

  /**
   * Empty-cart navigation logic
   */
  #initEmptyCartNav() {
    const nav = this.querySelector('[data-cart-empty-nav]');
    if (!nav) return;
    if (nav._cartNavBound) return;
    nav._cartNavBound = true;

    const panels = nav.querySelectorAll('.cart-empty-panel');

    const showPanel = (id) => {
      panels.forEach((panel) => {
        const isActive = panel.dataset.panelId === id;
        panel.classList.toggle('is-active', isActive);
      });
    };

    nav.addEventListener('click', (event) => {
      const targetPanel = event.target.closest('[data-panel-target]');
      const backPanel = event.target.closest('[data-back-target]');

      if (targetPanel) {
        event.preventDefault();
        showPanel(targetPanel.dataset.panelTarget);
      }

      if (backPanel) {
        event.preventDefault();
        showPanel(backPanel.dataset.backTarget);
      }
    });
  }

  async open() {
    await this.#refreshCartHtml();
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent =
        document.querySelector('shopify-payment-terms')?.shadowRoot;

      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

// Register component
if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
